/**
 * The theme's storage adapter: Firestore plus `/app`'s local key.
 *
 * Kept apart from `lib/theme.js` so that the decisions there stay pure and
 * testable and only this file knows about Firebase.
 *
 * ── Two-phase load, and why ───────────────────────────────────────────────
 * Reading the settings document is a network round trip. Waiting for it
 * before painting means a Slate user stares at a white screen for as long as
 * Firestore takes — legacy has exactly this bug, and worse: its boot overlay
 * is hardcoded `#FFFFFF` (app.html:13281), so every Slate user gets a white
 * flash on every single load.
 *
 * So: apply `/app`'s local copy synchronously the moment the uid is known,
 * then reconcile with the cloud when it answers. The local copy is written by
 * both programs, so in practice it is already right and the second phase
 * changes nothing.
 */

import { readDoc, mergeDoc } from './firebase.js'
import { wrap } from './settings.js'
import { applyTheme, normalizeTheme, readLegacyTheme, resolveTheme, writeLegacyTheme, THEME_KEY } from './theme.js'
import { reapplyBrand } from './brand-store.js'

/**
 * Phase one: paint the theme we can know without waiting.
 *
 * @param {string} uid
 * @returns {'grand'|'slate'} what was applied
 */
export function applyCachedTheme(uid) {
  return applyTheme(resolveTheme({ legacyLocal: readLegacyTheme(uid) }))
}

/**
 * Phase two: reconcile against the shared settings document.
 *
 * A failed read deliberately leaves the cached theme in place rather than
 * snapping to Grand. The advisor's screen going light on its own, because a
 * network call failed, is worse than being one round trip out of date.
 *
 * @param {string} uid
 * @returns {Promise<'grand'|'slate'>}
 */
export async function syncThemeFromCloud(uid) {
  const cached = resolveTheme({ legacyLocal: readLegacyTheme(uid) })
  try {
    const settings = await readDoc(['users', uid, 'settings', 'main'])
    const resolved = resolveTheme({ settings, legacyLocal: readLegacyTheme(uid) })
    if (resolved !== cached) {
      applyTheme(resolved)
      /* Same reason as `setTheme`: the brand's colours depend on the theme. */
      reapplyBrand()
      /* Keep `/app`'s copy honest too, so the next cold start is instant and
         the two programs do not disagree. */
      writeLegacyTheme(uid, resolved)
    }
    return resolved
  } catch (err) {
    console.error('theme sync failed, keeping the cached theme', err)
    return cached
  }
}

/**
 * Switch the theme and make it stick everywhere.
 *
 * Order matters: paint first, then persist. The advisor asked for a visual
 * change; making them wait on a network write to see it is the wrong trade,
 * and a failed write should not mean a dead button.
 *
 * @param {string} uid
 * @param {unknown} theme
 * @returns {Promise<'grand'|'slate'>}
 */
export async function setTheme(uid, theme) {
  const name = normalizeTheme(theme)
  applyTheme(name)
  /* The brand's colours are suppressed in Slate and applied in Grand, so the
     theme moving has to re-decide them — see `brand.js#BRAND_COLOUR_PROPS`.
     Without this, switching to Grand leaves the advisor's colours off until a
     reload, and switching to Slate leaves them on, dimming every menu row. */
  reapplyBrand()
  writeLegacyTheme(uid, name)

  try {
    /* `wrap` produces `{v, at}` — the same shape `/app` writes and reads, and
       the same shape its last-write-wins comparison expects. A bare string
       here would be read by `/app` as a value with no timestamp, which its
       `hydrateSettingsFromCloud` treats as "cloud wins" unconditionally. */
    await mergeDoc(['users', uid, 'settings', 'main'], {
      [THEME_KEY]: wrap(name, new Date().toISOString()),
    })
  } catch (err) {
    console.error('theme save failed; it is applied locally but will not follow you', err)
  }
  return name
}
